import {
  PresentationSnapshot,
  Variation,
} from '@sportngin/se-commerce-sdk/models/clare';
import {CartComparator} from './cart-comparator';
import {
  CartItemSpecification,
  CartItemSpecificationInterface,
} from './cart-item-specification';
import {Image} from './image';
import {ProductReference} from './product-reference';
import {Quote} from './quote';

export interface CartItemBundleSelection {
  productId: string;
  variantId?: string;
  specs?: CartItemSpecificationInterface[];
}

export interface CartItemInterface {
  productId: string;
  quantity: number;
  name?: string;
  image?: Image;
  /** Immutable Product or Bundle presentation captured when the item was added. */
  presentationSnapshot?: PresentationSnapshot;
  unitPrice?: number;
  description?: string;
  id?: string;
  specs?: CartItemSpecificationInterface[];
  variant?: Variation;
  quote?: Quote;
  references?: ProductReference;
  maxQuantity?: number;
  taxCode?: number;
  templateKey: string;
  itemVariationId?: number | string;
  variation?: CartItemVariation;
  fulfilledBy?: string;
  isPromotionalItem?: boolean;
  marketingOptIn?: boolean;
  bundleSelections?: CartItemBundleSelection[];
  /** Clare cart `bundle_id` for lines originating from a bundle add. */
  bundleId?: number;
  /** Clare bundle instance identifier for grouping child lines. */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  bundle_instance_id?: string;
  /** Display name of the parent bundle. */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  bundle_name?: string;
  /** Product id of the child line within a bundle. */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  bundled_product_id?: string;
  cartMetadata?: Record<string, unknown>;
  /** Synthetic cart-slide grouping for Clare bundle children. */
  bundleGroupItems?: CartItem[];
  /** Preformatted child lines for bundle cart-slide rendering. */
  bundleGroupLines?: string[];
}

export interface CartItemVariation {
  id: number;
  name: string;
}

export class CartItem implements CartItemInterface, CartComparator<CartItem> {
  productId = '';
  quantity = 1;
  name?: string;
  image?: Image;
  presentationSnapshot?: PresentationSnapshot;
  unitPrice?: number = 0;
  description?: string;
  id?: string = '';
  specs?: CartItemSpecification[];
  variant?: Variation;
  quote?: Quote;
  references?: ProductReference;
  maxQuantity?: number;
  taxCode?: number;
  templateKey: string;
  itemVariationId?: number | string;
  fulfilledBy?: string;
  isPromotionalItem? = false;
  marketingOptIn?: boolean;
  bundleSelections?: CartItemBundleSelection[];
  bundleId?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  bundle_instance_id?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  bundle_name?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  bundled_product_id?: string;
  cartMetadata?: Record<string, unknown>;
  bundleGroupItems?: CartItem[];
  bundleGroupLines?: string[];

  constructor(src?: CartItemInterface) {
    if (!src) return;

    this.productId = src.productId;
    this.quantity = src.quantity;
    this.name = src.name;
    this.image = src.image;
    this.presentationSnapshot = src.presentationSnapshot
      ? {
          ...src.presentationSnapshot,
          images: src.presentationSnapshot.images.map(image => ({...image})),
          logo: src.presentationSnapshot.logo
            ? {...src.presentationSnapshot.logo}
            : null,
        }
      : undefined;
    this.unitPrice = src.unitPrice;
    this.description = src.description;
    this.id = src.id;
    this.specs = src.specs
      ? src.specs.map(s => new CartItemSpecification(s))
      : [];
    this.variant = src.variant;
    this.quote = src.quote;
    this.references = src.references;
    this.maxQuantity = src.maxQuantity;
    this.taxCode = src.taxCode;
    this.templateKey = src.templateKey;
    this.itemVariationId = src.itemVariationId;
    this.fulfilledBy = src.fulfilledBy;
    this.isPromotionalItem = src.isPromotionalItem;
    this.marketingOptIn = src.marketingOptIn;
    this.bundleSelections = src.bundleSelections;
    this.bundleId = src.bundleId;
    this.bundle_instance_id = src.bundle_instance_id;
    this.bundle_name = src.bundle_name;
    this.bundled_product_id = src.bundled_product_id;
    this.cartMetadata = src.cartMetadata ? {...src.cartMetadata} : undefined;
    this.bundleGroupItems = src.bundleGroupItems
      ? src.bundleGroupItems.map(item => new CartItem(item))
      : undefined;
    this.bundleGroupLines = src.bundleGroupLines
      ? [...src.bundleGroupLines]
      : undefined;
  }

  price(): number {
    return (this.unitPrice || 0) * this.quantity;
  }

  /**
   * Determines CartItem equality based off the item's specifications. Used to
   * check if an item exists in the cart. Method does NOT perform a deep equality check.
   * @param a the CartItem this is being compared to.
   * @param b this
   */
  equals(a: CartItem, b: CartItem = this): boolean {
    if (!a || !b) return false;

    let result =
      a.productId === b.productId &&
      !!a.isPromotionalItem === !!b.isPromotionalItem;

    if (!result) return false;

    // Check Variations
    const aVariationId = a.itemVariationId || a.variant?.id;
    const bVariationId = b.itemVariationId || b.variant?.id;

    if (aVariationId || bVariationId) {
      result = Number(aVariationId) === Number(bVariationId);
    } else {
      // If no variations, check specs
      const hasASpecs = !!a.specs && a.specs.length > 0;
      const hasBSpecs = !!b.specs && b.specs.length > 0;

      if (hasASpecs && hasBSpecs && a.specs!.length === b.specs!.length) {
        result = this.#compareItemSpecs(a.specs!, b.specs!);
      } else if (hasASpecs || hasBSpecs) {
        result = false;
      }
    }

    if (result && (a.quote || b.quote)) {
      if (a.quote && b.quote) {
        result = a.quote.equals(b.quote);
      } else {
        result = false;
      }
    }

    if (result) {
      const aBundle = a.bundleId ?? null;
      const bBundle = b.bundleId ?? null;
      if (aBundle !== bBundle) {
        result = false;
      }
    }

    if (result) {
      const aBundleInstance = a.bundle_instance_id;
      const bBundleInstance = b.bundle_instance_id;
      if (aBundleInstance !== undefined || bBundleInstance !== undefined) {
        result =
          String(aBundleInstance ?? '') === String(bBundleInstance ?? '');
      }
    }

    return result;
  }

  #compareItemSpecs(
    a: CartItemSpecification[],
    b: CartItemSpecification[],
  ): boolean {
    let result = true;
    for (const spec of a) {
      result = b.findIndex(s => s.equals(spec)) > -1;
      if (!result) break;
    }
    return result;
  }
}
